import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Drawer, FloatButton, Collapse, Input, Button, Empty, Spin } from 'antd';
import { MessageOutlined, SendOutlined } from '@ant-design/icons';
import ReactECharts from 'echarts-for-react';
import axios, { getAuthorizationToken } from '../../utils/axios';

function parseSseFrames(buffer) {
  // Splits on the SSE frame delimiter and returns [frames, remainder] —
  // the remainder is an incomplete frame still awaiting more bytes.
  const parts = buffer.split('\n\n');
  const remainder = parts.pop();
  const frames = [];
  for (const block of parts) {
    if (!block.trim()) continue;
    let kind = null;
    let data = null;
    for (const line of block.split('\n')) {
      if (line.startsWith('event: ')) kind = line.slice('event: '.length);
      else if (line.startsWith('data: ')) {
        try {
          data = JSON.parse(line.slice('data: '.length));
        } catch (e) {
          data = null;
        }
      }
    }
    if (kind) frames.push({ kind, data });
  }
  return [frames, remainder];
}

function ToolCallStep({ toolCall }) {
  return (
    <Collapse
      size="small"
      style={{ marginTop: 8 }}
      items={[
        {
          key: toolCall.id,
          label: `${toolCall.name}${toolCall.status === 'running' ? ' — running…' : ''}`,
          children: (
            <div style={{ fontSize: 12, color: 'var(--text-secondary)', whiteSpace: 'pre-wrap' }}>
              {toolCall.resultSummary || 'Waiting for result…'}
            </div>
          ),
        },
      ]}
    />
  );
}

function ChatMessage({ message }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ fontWeight: 600, fontSize: 12, color: 'var(--text-secondary)', marginBottom: 4 }}>
        {message.role === 'user' ? 'You' : 'Analyst'}
      </div>
      <div style={{ whiteSpace: 'pre-wrap' }}>{message.content}</div>
      {(message.toolCalls || []).map((tc) => (
        <ToolCallStep key={tc.id} toolCall={tc} />
      ))}
      {(message.charts || []).map((option, idx) => (
        <div key={idx} style={{ marginTop: 12, border: '1px solid var(--border-color, #eee)', borderRadius: 8, padding: 8 }}>
          <ReactECharts option={option} />
        </div>
      ))}
    </div>
  );
}

export default function ChatPanel() {
  const [enabled, setEnabled] = useState(false);
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [streaming, setStreaming] = useState(false);
  const conversationIdRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    axios.get('/ai/features')
      .then((response) => {
        if (!cancelled) setEnabled(!!response.data?.agent_enabled);
      })
      .catch(() => {
        // Feature-flag check failing (e.g. logged out) should not surface an
        // error — just keep the panel hidden, same as "disabled".
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const sendMessage = useCallback(async () => {
    const text = input.trim();
    if (!text || streaming) return;
    setInput('');
    setStreaming(true);

    setMessages((prev) => [...prev, { role: 'user', content: text }]);
    const assistantIndex = messages.length + 1;
    setMessages((prev) => [...prev, { role: 'assistant', content: '', toolCalls: [], charts: [] }]);

    try {
      const token = await getAuthorizationToken();
      const response = await fetch('/ai/chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          conversation_id: conversationIdRef.current,
          message: text,
        }),
      });

      if (!response.ok || !response.body) {
        throw new Error(`Chat request failed: ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      const updateAssistant = (updater) => {
        setMessages((prev) => {
          const next = [...prev];
          next[assistantIndex] = updater(next[assistantIndex]);
          return next;
        });
      };

      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const [frames, remainder] = parseSseFrames(buffer);
        buffer = remainder;

        for (const frame of frames) {
          if (!frame.data) continue;
          switch (frame.kind) {
            case 'conversation':
              conversationIdRef.current = frame.data.conversation_id;
              break;
            case 'text_delta':
              updateAssistant((m) => ({ ...m, content: m.content + frame.data.text }));
              break;
            case 'tool_call':
              updateAssistant((m) => ({
                ...m,
                toolCalls: [...m.toolCalls, { id: frame.data.id, name: frame.data.name, status: 'running' }],
              }));
              break;
            case 'tool_result_summary':
              updateAssistant((m) => ({
                ...m,
                toolCalls: m.toolCalls.map((tc) =>
                  tc.id === frame.data.id
                    ? { ...tc, status: 'done', resultSummary: frame.data.preview }
                    : tc
                ),
              }));
              break;
            case 'chart':
              updateAssistant((m) => ({ ...m, charts: [...m.charts, frame.data.option] }));
              break;
            case 'done':
            default:
              break;
          }
        }
      }
    } catch (err) {
      setMessages((prev) => {
        const next = [...prev];
        next[assistantIndex] = {
          ...next[assistantIndex],
          content: next[assistantIndex].content || 'Something went wrong — please try again.',
        };
        return next;
      });
    } finally {
      setStreaming(false);
    }
  }, [input, streaming, messages.length]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  if (!enabled) {
    return null;
  }

  return (
    <>
      <FloatButton
        icon={<MessageOutlined />}
        type="primary"
        tooltip="Ask the analyst agent"
        onClick={() => setOpen(true)}
      />
      <Drawer
        open={open}
        onClose={() => setOpen(false)}
        title="Analyst agent"
        width={480}
      >
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          <div style={{ flex: 1, overflowY: 'auto', marginBottom: 12 }}>
            {messages.length === 0 ? (
              <Empty description="Ask about your latest analysis results" />
            ) : (
              messages.map((m, idx) => <ChatMessage key={idx} message={m} />)
            )}
            {streaming && <Spin size="small" />}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <Input.TextArea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="What did the latest analysis find?"
              autoSize={{ minRows: 1, maxRows: 4 }}
              disabled={streaming}
            />
            <Button
              type="primary"
              icon={<SendOutlined />}
              onClick={sendMessage}
              loading={streaming}
              disabled={!input.trim()}
            />
          </div>
        </div>
      </Drawer>
    </>
  );
}
